import { Navigate, useLocation, useMatches, useNavigate, useSearch } from '@tanstack/react-router'
import { Suspense, useEffect, type ReactNode } from 'react'
import { useSessionUserQuery } from '@/hooks/use-session'
import { resolveRouteAccess, type RouteRedirect } from '@/route-access'
import { resolveEntryRedirect } from '@/entry-access'
import { postLoginHref, type PostLoginDestination } from 'shared/post-login'
import { MatchedRoutePending, type ShellId } from './shell-pending'

/**
 * The one access guard both shells wrap their outlet in. The session read sits BELOW its own boundary, never above
 * the outlet: the shell renders on the first byte and the route's skeleton streams inside it, instead of the whole app
 * waiting as the shell's fallback for as long as the session takes (measured on production as a blank, then a
 * spinner, on every refresh).
 */
export function Guarded(props: { shell: ShellId; children: ReactNode }) {
  return (
    <Suspense fallback={<MatchedRoutePending shell={props.shell} />}>
      <AccessGuard shell={props.shell}>{props.children}</AccessGuard>
    </Suspense>
  )
}

function AccessGuard(props: { shell: ShellId; children: ReactNode }) {
  const matches = useMatches()
  const routeId = matches[matches.length - 1]?.routeId ?? ''
  // Resolves the session user (already loaded by the root) + the active route against one policy and redirects
  // BEFORE the route's auth/context-gated query mounts — so the gated query never fires, SSR never throws, and no
  // route component carries its own <Navigate>. getUser is read by the root above us, so this is a cache hit.
  const { data: user, isFetching: sessionRevalidating } = useSessionUserQuery()
  const location = useLocation()
  // `strict: false`: this guard sits above every route, so it reads whatever the matched one declared — `redirect`
  // is the login route's own search, and `useLocation().search` is not parsed against it up here.
  const entrySearch = useSearch({ strict: false }) as { redirect?: string; ref?: string }
  const redirect = resolveRouteAccess({ routeId, user, href: location.href })
  // The mirror of the policy above: /login and /register are for visitors who are not signed in yet, so a
  // finished user who lands on one goes where they would have gone had they just signed in (see entry-access).
  const entry = resolveEntryRedirect({
    routeId,
    user,
    redirect: entrySearch.redirect,
    invitationToken: entrySearch.ref,
  })
  // Both redirects wait out a revalidating session, which still describes the pre-write viewer — docs/notes/app/app-shell.md.
  const pending = <MatchedRoutePending shell={props.shell} />
  if (redirect) return sessionRevalidating ? pending : <AccessRedirect redirect={redirect} />
  if (entry) return sessionRevalidating ? pending : <EntryRedirect shell={props.shell} destination={entry} />
  return props.children
}

/** Imperative, not `<Navigate>`: a post-login destination may be an opaque href, which that component cannot take.
 *  The href string is the effect's dependency, so a fresh object each render cannot re-fire it. */
function EntryRedirect(props: { shell: ShellId; destination: PostLoginDestination }) {
  const navigate = useNavigate()
  const href = postLoginHref(props.destination)
  useEffect(() => {
    navigate({ href, replace: true })
  }, [navigate, href])
  return <MatchedRoutePending shell={props.shell} />
}

// Render the typed <Navigate> for a resolved access redirect. The discriminated union keeps each `to`
// a route-tree literal (so TanStack type-checks it), and only /login carries the return `redirect`.
function AccessRedirect(props: { redirect: RouteRedirect }) {
  if (props.redirect.to === '/login')
    return <Navigate to="/login" search={{ redirect: props.redirect.redirect }} replace />
  if (props.redirect.to === '/employees') return <Navigate to="/employees" replace />
  if (props.redirect.to === '/employees/$employeeId/report')
    return <Navigate to="/employees/$employeeId/report" params={{ employeeId: props.redirect.employeeId }} replace />
  if (props.redirect.to === '/report') return <Navigate to="/report" replace />
  return <Navigate to={props.redirect.to} replace />
}
